import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Grid,
  CardHeader,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItemText,
  Avatar,
  Chip,
  Button,
  Tooltip,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import {
  ExpandMore as ExpandMoreIcon,
  Done as DoneIcon,
  LocalAtm as RefundIcon,
} from '@material-ui/icons';
import PropTypes from 'prop-types';
import { formatCurrency } from 'src/utils/currency';
import { formatDateISOWithSeconds } from 'src/utils/date';
import { isRetailPaymentRefundable } from 'src/utils/retailPaymentStatusTypes';
import RefundDialog from '../modals/RefundDialog';
import { FeatureProvider } from 'src/context/FeatureContext';
import RetailPaymentService from 'src/services/retailPaymentService'
import { createAxiosInstance } from 'src/utils/axios';

const retailPaymentService = new RetailPaymentService(
    createAxiosInstance(process.env.REACT_APP_CASH_QORE_API)
);

const useStyles = makeStyles((theme) => ({
  card: {
    marginBottom: theme.spacing(2),
  },
  fontWeightMedium: {
    fontWeight: theme.typography.fontWeightMedium,
  },
  refundColumn: {
    flexBasis: '33.33%',
    paddingLeft: theme.spacing(2),
    float: 'left',
  },
  fullRefundAccordion: {
    backgroundColor: '#e8f5e9',
    color: '#1b5e20',
  },
  refundHeaderTitle: {
    display: 'flex',
    justifyContent: 'space-between',
  },
}));

function RefundInfo({
  retailPayment,
  refundfeatureIdentifier,
  refunds,
  onRefundSuccess,
}) {

  const classes = useStyles();
  const theme = useTheme();
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const isRefundsAllowed = retailPayment && isRetailPaymentRefundable(retailPayment?.RetailPaymentStatusId); // 8 = Captured, 9 = PartialRefund
  const subtotal = retailPayment?.PaymentDetail?.SubTotal ?? 0;
  const total = retailPayment?.PaymentDetail?.Total ?? 0;
  const refundedTotalAmount = refunds?.reduce((acc, refund) => acc + refund.RefundAmount, 0) ?? 0;
  const unrefundedAmount = total - refundedTotalAmount;

  return (
    <>
      <Card className={classes.card}>
        <CardHeader
          style={{ color: theme.palette.secondary.main }}
          title={
            <div className={classes.refundHeaderTitle}>
              <Typography>Refund Info</Typography>
              <FeatureProvider
                featureIdentifier={refundfeatureIdentifier}
              >
                <div>
                  <Tooltip 
                    title="Refund not available until the payment has been Captured." 
                    disableHoverListener={isRefundsAllowed} 
                  >
                  <span>
                    <Button 
                      variant='contained' 
                      color='secondary' 
                      onClick={() => setShowRefundDialog(true)}
                      disabled={!isRefundsAllowed}
                    >
                      Refund Payment
                    </Button>
                  </span>
                  </Tooltip>
                </div>
              </FeatureProvider>
            </div>} />
        <Divider />
        <CardContent>
          <Grid container spacing={4}>
            <Grid item xs={12}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className={classes.fontWeightMedium}>Total Refunded Amount</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="textSecondary">
                      {formatCurrency(refundedTotalAmount)?? '$0.00'}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className={classes.fontWeightMedium}>Non-Refunded Amount</TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="textSecondary">
                      {formatCurrency(unrefundedAmount)}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>  
            </Grid>
          </Grid>
          <Grid container alignItems="center" spacing={4}>
            <Grid item md={12} xs={12}>
              {(!refunds || refunds.length < 1) &&
                <Typography>
                  No Refunds
                </Typography>
              }
              {refunds?.map((refund, index) => (
                <Accordion key={`refund_${index}`} className={refund?.IsFullRefund && classes.fullRefundAccordion}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <div className={classes.refundColumn}>
                      <Avatar>
                        <RefundIcon />
                      </Avatar>
                    </div>
                    <ListItemText
                      primary={formatCurrency(refund?.RefundAmount)}
                      secondary={formatDateISOWithSeconds(refund?.RefundDate)}
                    />
                    <div style={{ margin: '5px' }}>
                      {refund?.IsFullRefund &&
                        <Chip
                          icon={<DoneIcon />}
                          label="Full Refund"
                          clickable
                          color="primary"
                          deleteIcon={<DoneIcon />}
                          variant="outlined"
                        />
                      }
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List key={`refund_${index}`} className={classes.root} style={{ width: '100%' }}>
                      <Divider />
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell className={classes.fontWeightMedium}>Stripe Refund Id</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="textSecondary">
                                {refund?.StripeRefundId}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.fontWeightMedium}>Retail Payment Refund Id</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="textSecondary">
                                {refund?.RetailPaymentRefundId}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.fontWeightMedium}>Type</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="textSecondary" component="div">
                                {refund?.IsFullRefund ? 'Full ' : 'Partial '} Refund
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.fontWeightMedium}>User</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="textSecondary" component="div">
                                {refund?.RefundUser}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.fontWeightMedium}>Reason</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="textSecondary">
                                {refund?.RefundReason}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell className={classes.fontWeightMedium}>Notes</TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" color="textSecondary">
                                {refund?.RefundNote ?? '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {showRefundDialog && <RefundDialog isDialogOpen={showRefundDialog} setIsDialogOpen={setShowRefundDialog} handleClose={() => setShowRefundDialog(false)} retailPayment={retailPayment} retailPaymentService={retailPaymentService} onSuccess={onRefundSuccess} />}
    </>
  );
}

const refundShape = PropTypes.shape({
  RetailPaymentRefundId: PropTypes.string,
  RetailPaymentId: PropTypes.string,
  StripeRefundId: PropTypes.string,
  RefundDate: PropTypes.string,
  RefundAmount: PropTypes.number,
  RefundReasonId: PropTypes.number,
  RefundReason: PropTypes.string,
  RefundUser: PropTypes.string,
  RefundNote: PropTypes.string,
  IsFullRefund: PropTypes.bool,
  TransactionId: PropTypes.string.isRequired,
});

const paymentDetailsShape = PropTypes.shape({
  PoNumber: PropTypes.string.isRequired,
  RetailPaymentId: PropTypes.number.isRequired,
  DateCreated: PropTypes.string.isRequired,
  Caller: PropTypes.shape({
    FirstName: PropTypes.string.isRequired,
    LastName: PropTypes.string.isRequired,
    Email: PropTypes.string.isRequired,
  }).isRequired,
  StripePaymentId: PropTypes.string.isRequired,
  StripeInvoiceId: PropTypes.string.isRequired,
  StripeDisputeId: PropTypes.string,
  DisputeDate: PropTypes.string,
  PaymentDetail: PropTypes.shape({
    SubTotal: PropTypes.number.isRequired,
    Tax: PropTypes.number.isRequired,
    Total: PropTypes.number.isRequired,
    Currency: PropTypes.string.isRequired,
  }).isRequired,
  Refunds: PropTypes.arrayOf(PropTypes.shape({
    IsFullRefund: PropTypes.bool.isRequired,
    RefundAmount: PropTypes.number.isRequired,
    RefundDate: PropTypes.string.isRequired, // ISO date string
    RefundNote: PropTypes.string.isRequired,
    RefundReason: PropTypes.string.isRequired,
    RefundReasonId: PropTypes.number.isRequired,
    RefundUser: PropTypes.string.isRequired,
    RetailPaymentId: PropTypes.string.isRequired,
    RetailPaymentRefundId: PropTypes.string.isRequired,
    StripeRefundId: PropTypes.string.isRequired,
    TransactionId: PropTypes.string,
  })).isRequired,
});


RefundInfo.propTypes = {
  retailPayment: PropTypes.objectOf(paymentDetailsShape).isRequired,
  refunds: PropTypes.arrayOf(refundShape),
  onRefundSuccess: PropTypes.func,
};

export default RefundInfo;
